import { Controller } from "@hotwired/stimulus";
import * as d3 from "d3";
class default_1 extends Controller {
    constructor() {
        super(...arguments);
        this.title = null;
        this.currentNode = null;
        this.svg = null;
        this.g = null;
        this.width = 600;
        this.margin = { top: 20, right: 20, bottom: 30, left: 150 };
        this.BAR_HEIGHT = 30;
        this.BAR_PADDING = 0.1;
    }
    connect() {
        this.renderChart();
    }
    renderChart() {
        const data = this.dataValue;
        if (!data)
            return;
        this.svg = d3.select(this.element).append("svg").attr("width", this.width);
        this.g = this.svg
            .append("g")
            .attr("transform", `translate(${this.margin.left},${this.margin.top})`);
        this.title = this.g
            .append("text")
            .attr("class", "chart-title")
            .attr("x", (this.width - this.margin.left - this.margin.right) / 2)
            .attr("y", -6)
            .attr("text-anchor", "middle")
            .attr("font-weight", "bold")
            .attr("font-size", "14px");
        this.backButton = d3
            .select(this.element)
            .insert("button", ":first-child")
            .text("<")
            .attr("class", "btn btn-primary")
            .style("margin-bottom", "10px")
            .style("display", "none")
            .on("click", () => {
            if (this.currentNode && this.currentNode.parent) {
                this.update(this.currentNode.parent);
            }
        });
        const root = d3.hierarchy(data).sum((d) => d.value || 0);
        this.update(root);
    }
    update(source) {
        if (!this.g || !this.svg)
            return;
        this.title?.text(source.depth > 0 ? source.data.name ?? "" : "");
        this.currentNode = source;
        this.backButton?.style("display", source.depth > 0 ? "inline-block" : "none");
        const nodes = source.children || [];
        const innerWidth = this.width - this.margin.left - this.margin.right;
        const innerHeight = nodes.length * this.BAR_HEIGHT;
        const height = innerHeight + this.margin.top + this.margin.bottom;
        this.svg.attr("height", height);
        const x = d3.scaleLinear().range([0, innerWidth]);
        const y = d3.scaleBand().padding(this.BAR_PADDING);
        const values = nodes
            .map((d) => d.value)
            .filter((v) => typeof v === "number");
        y.domain(nodes.map((d) => d.data.name)).range([0, innerHeight]);
        x.domain([0, d3.max(values) || 0]);
        // Bars
        const bars = this.g
            .selectAll("rect")
            .data(nodes, (d) => d.data.name);
        bars.exit().remove();
        const barEnter = bars.enter().append("rect");
        barEnter
            .merge(bars)
            .attr("y", (d) => y(d.data.name) || 0)
            .attr("height", y.bandwidth())
            .attr("x", 0)
            .attr("width", (d) => x(d.value || 0))
            .attr("fill", (d) => source.parent
            ? "#ffb82d" // on child (brand_model) level
            : "#004988" // on root (brand) level
        )
            .style("cursor", source.parent ? "default" : "pointer")
            .on("click", async (event, d) => {
            if (source.parent)
                return; // only clickable at root
            if (!d.children && d.data.id) {
                const resp = await fetch(`/admin/statistics/brand_models?brand_id=${d.data.id}`);
                const models = await resp.json();
                const childNode = d3.hierarchy({
                    name: d.data.name,
                    children: models,
                });
                childNode.sum((n) => n.value ?? 0);
                childNode.children?.forEach((c) => (c.parent = d));
                d.children = childNode.children;
            }
            this.update(d);
        });
        // Labels
        const labels = this.g
            .selectAll("text.bar-label")
            .data(nodes, (d) => d.data.name);
        labels.exit().remove();
        labels
            .enter()
            .append("text")
            .attr("class", "bar-label")
            .merge(labels)
            .attr("x", 0)
            .attr("y", (d) => (y(d.data.name) || 0) + y.bandwidth() / 2)
            .attr("dy", "0.35em")
            .attr("dx", -5)
            .attr("text-anchor", "end")
            .text((d) => d.data.name);
        // Value labels (count at end of bar)
        const valueLabels = this.g
            .selectAll("text.value-label")
            .data(nodes, (d) => d.data.name);
        valueLabels.exit().remove();
        valueLabels
            .enter()
            .append("text")
            .attr("class", "value-label")
            .merge(valueLabels)
            .attr("x", (d) => x(d.value || 0) + 5) // 5px after the bar
            .attr("y", (d) => (y(d.data.name) || 0) + y.bandwidth() / 2)
            .attr("dy", "0.35em")
            .attr("text-anchor", "start")
            .attr("font-size", "12px")
            .text((d) => d.value || 0);
    }
}
default_1.values = { data: Object };
export default default_1;
